import { Component, ViewChild, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { LoaderType, LoadingService } from '../../services/loading.service';
import { FileUtilService } from '../../services/file-util.service';
import { PersonService } from '../../services/person.service';
import { ToastrService } from 'ngx-toastr';
import { DxFormComponent } from 'devextreme-angular/ui/form';
import { DxSelectBoxTypes } from 'devextreme-angular/ui/select-box';
import { TranslateService } from '@ngx-translate/core';
import validationEngine from 'devextreme/ui/validation_engine';
import { Observable, Subject, debounceTime, distinctUntilChanged, forkJoin, iif, mergeMap, of, switchMap, tap } from 'rxjs';
import { GoogleMapsService } from '../../services/google-maps.service';
import { DxDropDownBoxComponent, DxFileUploaderComponent } from 'devextreme-angular';
import { TrainingTypeModel } from "../../models/training/training-type.model";
import { TrainingCategoryModel } from 'src/app/models/training-category/training-category.model';
import { TrainingCategoryService } from 'src/app/services/training-categories/training-category.service';
import { ApprovalRequestExternalCertificateSummaryModel } from 'src/app/models/approval-request/approval-request-external-certificate-summary.model';
import { ApprovalRequestExternalCertificateCreationAndUpdateModel } from 'src/app/models/approval-request/approval-request-external-certificate-creation-and-update.model';
import { ApprovalRequestService } from 'src/app/services/approval-request.service';
import { CompletedEventService } from 'src/app/services/completed-event.service';
import { IDD_MIN_TRAINING_DURATION_MINUTES } from 'src/app/constants/idd.constants';

@Component({
  selector: 'app-update-external-certificate-request-dialog',
  standalone: false,
  templateUrl: './update-external-certificate-request-dialog.component.html',
  styleUrl: './update-external-certificate-request-dialog.component.scss'
})
export class UpdateExternalCertificateRequestDialogComponent implements OnInit {
  @ViewChild(DxFileUploaderComponent, { static: false })
  fileUploader!: DxFileUploaderComponent;

  @ViewChild('updateRequestForm', { static: true })
  updateRequestForm!: DxFormComponent;

  @ViewChild(DxDropDownBoxComponent, { static: false })
  trainingGroupDropDown!: DxDropDownBoxComponent;

  trainingCategoryDropDown!: DxDropDownBoxComponent;
  trainingTypeOptions!: DxSelectBoxTypes.Properties;

  isDropZoneActive = false;
  isCertificateFile = false;
  skipAiProcessing = false;

  @Input()
  editingPersonId!: string;

  @Input()
  externalRequest!: ApprovalRequestExternalCertificateSummaryModel;

  @Output()
  onRequestUpdated = new EventEmitter();

  form = new UntypedFormGroup({
    name: new UntypedFormControl(null, [Validators.required]),
    description: new UntypedFormControl(null, []),
    trainingType: new UntypedFormControl(null, [Validators.required]),
    iddHours: new UntypedFormControl(0, [Validators.required]),
    iddMinutes: new UntypedFormControl(0, [Validators.required]),
    date: new UntypedFormControl(null, [Validators.required]),
    location: new UntypedFormControl(null, []),
    trainingCategoryId: new UntypedFormControl(null, []),
    certificateFile: new UntypedFormControl(null, []),
  });

  externalRequestForm: ApprovalRequestExternalCertificateCreationAndUpdateModel 
    = <ApprovalRequestExternalCertificateCreationAndUpdateModel>{};

  trainingCategories: TrainingCategoryModel[] = [];
  googleSearchText: string = '';
  googlePlaces: any[] = [];
  isEditModeFile: boolean = false;

  private googlePlaceinputChanged = new Subject<string>();
  googleAutocompleteResults$ = this.googlePlaceinputChanged.pipe(
    debounceTime(300),
    distinctUntilChanged(),
    switchMap((searchTerm) => this.mapsService.getAutocomplete(searchTerm))
  );

  constructor(
    private trainingCategoryService: TrainingCategoryService,
    private toastrService: ToastrService,
    private fileUtilService: FileUtilService,
    private loadingService: LoadingService,
    private translateService: TranslateService,
    private mapsService: GoogleMapsService,
    private approvalRequestService: ApprovalRequestService,
    private completedEventService: CompletedEventService,
  ) {
    this.onSelectedFileChange = this.onSelectedFileChange.bind(this);
    this.autocompleteSearchFilter = this.autocompleteSearchFilter.bind(this);
  }

  ngOnInit(): void {
    const trainingCategories$ = this.trainingCategoryService.getAllTrainingCategories();
    let fileLoadingObservable$ = null;
  
    if (this.externalRequest.file) {
      fileLoadingObservable$ = new Observable<File>(observer => {
        new Promise<void>(resolve => setTimeout(resolve, 1000))
          .then(() => fetch(this.externalRequest.file!.fileUrl))
          .then(response => response.blob())
          .then(blob => {
            const file = new File([blob], this.externalRequest.file!.fileName, { type: blob.type });
            observer.next(file);
            observer.complete();
          })
          .catch(error => observer.error(error));
      });
    }
    
    const observables: any = { trainingCategories: trainingCategories$ };
    if (fileLoadingObservable$) {
      observables['certificateFile'] = fileLoadingObservable$;
    }
  
    this.loadingService.load(forkJoin(observables)).subscribe((result: any) => {
      this.trainingCategories = result.trainingCategories;
      this.setDropdownOptions();
  
      if (result.certificateFile && this.fileUploader) {
        this.fileUploader.value = [result.certificateFile];
        this.isCertificateFile = true;
        this.isEditModeFile = true;
      }
      
      this.googleAutocompleteResults$.subscribe((results) => {
        this.googlePlaces = results;
      });
  
      this.externalRequestForm.name = this.externalRequest.eventName;
      this.externalRequestForm.description = this.externalRequest.eventDescription;
      this.externalRequestForm.trainingType = this.externalRequest.trainingType;
      this.externalRequestForm.iddTimeMinutes = this.externalRequest.iddTimeMinutes;
      this.externalRequestForm.date = new Date(this.externalRequest.eventDate);
      this.externalRequestForm.trainingCategoryId =
        this.externalRequest.trainingCategory
          ? this.externalRequest.trainingCategory.id
          : null;
      this.externalRequestForm.location = this.externalRequest.location;
      this.externalRequestForm.iddHours = this.externalRequestForm.iddTimeMinutes
        ? Math.floor(this.externalRequestForm.iddTimeMinutes / 60)
        : 0;
      this.externalRequestForm.iddMinutes = this.externalRequestForm.iddTimeMinutes
        ? this.externalRequestForm.iddTimeMinutes % 60
        : 0;
      this.googleSearchText = this.externalRequest.location || '';
    });
  }

  setDropdownOptions() {
    const defaultSelectOptions = {
      showClearButton: true,
      stylingMode: 'outlined',
      displayExpr: 'name',
      valueExpr: 'id',
    } as DxSelectBoxTypes.Properties;

    this.trainingTypeOptions = {
      ...defaultSelectOptions,
      dataSource: [
        {
          id: 0,
          name: this.translateService.instant(
            'COMMON.FIELDS.TRAINING-TYPES.IDD'
          ),
        },
        {
          id: 1,
          name: this.translateService.instant(
            'COMMON.FIELDS.TRAINING-TYPES.COMPLIANCE'
          ),
        },
        {
          id: 2,
          name: this.translateService.instant(
            'COMMON.FIELDS.TRAINING-TYPES.GENERAL'
          ),
        },
      ],
      placeholder: '',
    };
  }

  async onSelectedFileChange(e: any) {
    if (e[0] && e[0].name.endsWith('.pdf')) {
      this.externalRequestForm.certificateFile =
        await this.fileUtilService.convertFileToRawFileModel(e[0]);

        if (!this.isEditModeFile && !this.skipAiProcessing) {
          this.loadingService
          .load(
            this.completedEventService.extractCertificateFile(
              this.externalRequestForm.certificateFile
            ),
            'aiLoader' as LoaderType
          )
          .subscribe((certificateData) => {
            this.externalRequestForm.name = certificateData.title;
            this.externalRequestForm.description = certificateData.description;
            this.externalRequestForm.trainingType = TrainingTypeModel[certificateData.training_type as keyof typeof TrainingTypeModel];
            this.externalRequestForm.date = new Date(certificateData.date);
            this.externalRequestForm.location = certificateData.location;
            this.googleSearchText = certificateData.location;
            this.externalRequestForm.iddHours = certificateData.idd_time ? Math.floor(
              certificateData.idd_time / 60
            ) : 0;
            this.externalRequestForm.iddMinutes = certificateData.idd_time ? certificateData.idd_time % 60 : 0;

            const trainingCategory = this.trainingCategories.find(category => category.name === certificateData.training_category);
            if (trainingCategory) {
              this.externalRequestForm.trainingCategoryId = trainingCategory.id;
            }
          });
        } else {
          this.isEditModeFile = false;
        }
    } else {
      this.externalRequestForm.certificateFile = null;
    }
    this.isDropZoneActive = false;
    this.skipAiProcessing = false;
  }

  removeFile() {
    this.externalRequestForm.certificateFile = null;
    this.fileUploader.instance.reset();

    if (this.isCertificateFile) {
      this.loadingService
        .load(
          this.approvalRequestService.deleteCertificateFile(this.externalRequest.id)
        )
        .subscribe(() => {
          this.isCertificateFile = false;
        });
    }
  }

  onTrainingCategorySelected(e: any) {
    if (e.node.key) {
      this.externalRequestForm.trainingCategoryId = e.node.key;
    }
  }

  onFormSubmit() {
    const isFormValid = this.isFormValid();
    if (isFormValid) {
      const externalRequestModel: ApprovalRequestExternalCertificateCreationAndUpdateModel = this.prepareRequestModel();

      this.loadingService
        .load(
          this.approvalRequestService.updateApprovalRequestExternalCertificate(
            this.externalRequest.id,
            externalRequestModel
          )
        )
        .subscribe({
          next: () => {
            this.toastrService.success(
              this.translateService.instant(
                'APPROVAL-REQUEST.APPROVAL-REQUEST-UPDATED'
              )
            );
            this.onRequestUpdated.emit();
          },
          error: () => {
            this.toastrService.error(
              this.translateService.instant(
                'COMMON.AN-ERROR-HAS-OCCURRED'
              )
            );
          },
        });
    }
  }

  handleTimePicker(value: { hours: number; minutes: number }) {
    this.externalRequestForm.iddHours = value.hours;
    this.externalRequestForm.iddMinutes = value.minutes;
  }

  autocompleteSearchFilter(e: any) {
    return this.googleSearchText;
  }

  getGooglePlaces(e: any) {
    this.googlePlaceinputChanged.next(e.value);
  }

  setLocationPlace(e: any) {
    this.loadingService
      .load(this.mapsService.getDetails(e.itemData.place_id))
      .subscribe((details) => {
        const location = details.formatted_address.startsWith(details.name)
          ? details.formatted_address
          : details.name + ', ' + details.formatted_address;
        this.externalRequestForm.location = location;
      });
  }

  onDropZoneEnter({ component, dropZoneElement, event }: any) {
    this.resetSkipAI();

    if (dropZoneElement.id === 'dropzone-external') {
      this.isDropZoneActive = true;
    }
  }

  onDropZoneLeave(e: any) {
    if (e.dropZoneElement.id === 'dropzone-external') { this.isDropZoneActive = false; }
  }

  onUpload(event: any) {
    console.log('Uploaded files:', event);
  }

  resetSkipAI(event?: any) {
    if (event && event.pointerId !== -1) {
      this.skipAiProcessing = false;
    }
  }

  onUploadSkipAI() {
    this.skipAiProcessing = true;
    document.getElementById('dropzone-external')?.click();  
  }

  onSubmitRequest() {
    this.onRequestUpdated.emit();
  }

  isTimeValid(): boolean {
    const isHoursValid = (
      this.externalRequestForm.iddHours && 
      this.externalRequestForm.iddHours > 0
    );

    const isMinutesValid = (
      this.externalRequestForm.iddMinutes && 
      this.externalRequestForm.iddMinutes >= IDD_MIN_TRAINING_DURATION_MINUTES
    );

    return !!(isHoursValid || isMinutesValid);
  }

  isFormValid(): boolean {
      const isCertificateFormValid =
        this.updateRequestForm.instance.validate().isValid;
      const isCustomFieldsValid =
        validationEngine.validateGroup('customFields').isValid;
  
      let isFormValid: boolean = !!(isCertificateFormValid && isCustomFieldsValid);
  
      if(this.externalRequestForm.trainingType === TrainingTypeModel.IDD && !this.isTimeValid()) {
        return false;
      }
  
      return isFormValid;
    }

  prepareRequestModel() {
    const date: Date = this.externalRequestForm.date!;
    date.setHours(12);

    if (this.externalRequestForm.trainingType !== 0) {
      this.externalRequestForm.iddTimeMinutes = 0;
    } else {
      this.externalRequestForm.iddTimeMinutes =
        this.externalRequestForm.iddHours! * 60 +
        this.externalRequestForm.iddMinutes!;
    }

    const requestModel: ApprovalRequestExternalCertificateCreationAndUpdateModel = {
      ...this.externalRequestForm,
      date,
    };

    if (!requestModel.trainingCategoryId) {
      requestModel.trainingCategoryId = null;
    }

    // delete requestModel.iddHours;
    // delete requestModel.iddMinutes;

    return requestModel;
  }
}
